import { layerClass } from "@freightsimple/ui-core/Theme/layers";
import { useWidth } from "@/Shared/Hooks/useWidth";
import Fade from "@/Shared/Animations/CustomComponents/Fade";
import RevealBox from "@/Shared/Layout/CustomComponents/RevealBox";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import moment from "moment";
import ExclusiveRateBadge from "./QuotesExclusiveRateBadgeCustomComponent";
import QuotesDownloadQuotePdf from "@/Shared/Quotes/CustomComponents/QuotesDownloadQuotePdf";
import QuotesRowBody from "./QuotesRowBody";
import QuotesRowHeader from "./QuotesRowHeader";
import type { QuoteWithOriginalPrice } from "@/Shared/Coupons/Hooks/useCoupons";

interface QuoteRowProps {
  pickupDate: moment.Moment;
  pickupLocation: Location | AddLocationLocationType | PreQuoteLocation;
  pickupDeadline: string | undefined;
  lineItems: Array<LineItem | AddLineItemType>;
  deliveryLocation: Location | AddLocationLocationType | PreQuoteLocation;
  deliveryDeadline: string | undefined;
  quote: Quote;
  onSelectQuote: () => void;
  notes: string | undefined;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
}
function QuotesRow(props: QuoteRowProps) {
  const deliveryDate = props.quote.expectedDeliveryDate
    ? moment(props.quote.expectedDeliveryDate)
    : undefined;

  // Published as a class (like the book flow's .narrow-cards) so the header,
  // body and columns can respond with `in-[.quote-card-narrow]:` variants.
  // Measured with useWidth, NOT a CSS container query — containment would
  // re-base fixed-position popovers inside the card.
  const [rowRef, rowWidth] = useWidth();
  // Four bands. Full-size one-line needs ~1120px (logo 206 + fixed
  // service/transit/price columns + a full-width date). Below that the
  // medium band keeps the single line by shrinking the logo, fonts, columns
  // and gaps; once even that stops fitting (~860) the narrow band lays the
  // fields out as a two-column grid (price back up top-right); once two
  // columns can't fit (~560) the tiny band stacks them in one column.
  const narrow = rowWidth > 0 && rowWidth < 860;
  const medium = rowWidth >= 860 && rowWidth < 1120;
  const tiny = rowWidth > 0 && rowWidth < 560;

  return (
    <div
      ref={rowRef}
      /* w-full is load-bearing: the quotes screen centers rows with an
       * items-center Stack, where a widthless block shrink-wraps to its
       * content — every card would take a different width. */
      className={`relative w-full ${narrow ? "quote-card-narrow" : ""} ${
        medium ? "quote-card-medium" : ""
      } ${tiny ? "quote-card-tiny" : ""}`}
    >
      {props.quote.isCustomerSpecificPricing && (
        /* Anchored to the top-left of the row so the pill straddles the box
         * border (half above, half overlapping the white card). The offsets
         * place it just inside the left edge near the carrier logo, per the
         * approved Linear design. */
        <div className={`absolute -top-[10px] left-4 ${layerClass.raised}`}>
          <ExclusiveRateBadge />
        </div>
      )}
      <RevealBox
        header={(open) => {
          return (
            <QuotesRowHeader
              carrierIdentifier={props.quote.carrierIdentifier}
              serviceDisplayName={props.quote.serviceDisplayName}
              showSecondaryInfo={!open}
              price={props.quote.price}
              taxAmount={props.quote.taxAmount}
              deliveryDate={deliveryDate}
              earliestTransitDays={props.quote.transitBusinessDays}
              latestTransitDays={props.quote.latestTransitBusinessDays}
              currency={props.quote.currency}
              notes={props.quote.notes}
              priceBeforeCoupon={
                (props.quote as QuoteWithOriginalPrice).priceBeforeCoupon
              }
              transitBusinessDaysDescription={
                props.quote.transitBusinessDaysDescription
              }
              restrictions={props.quote.restrictions}
            />
          );
        }}
        body={(open) => {
          return (
            <Fade show={open}>
              <QuotesRowBody
                pickupDeadline={props.pickupDeadline}
                pickupDate={props.pickupDate}
                pickupLocation={props.pickupLocation}
                lineItems={props.lineItems}
                deliveryLocation={props.deliveryLocation}
                deliveryDeadline={props.deliveryDeadline}
                quote={props.quote}
                onSelectQuote={props.onSelectQuote}
                notes={props.notes}
                addInsuranceToShipment={props.addInsuranceToShipment}
                insuranceAmount={props.insuranceAmount}
                insuranceCurrency={props.insuranceCurrency}
                stateOfGoods={props.stateOfGoods}
                priceBeforeCoupon={
                  (props.quote as QuoteWithOriginalPrice).priceBeforeCoupon
                }
                pickupReferenceNumber={props.pickupReferenceNumber}
                deliveryReferenceNumber={props.deliveryReferenceNumber}
                deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
                deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
              />
            </Fade>
          );
        }}
        footer={<QuotesDownloadQuotePdf quoteId={props.quote.quoteId} />}
      />
    </div>
  );
}
export default QuotesRow;
